<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="close" />

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
        <form @submit.prevent="submitReport">
          <!-- Header -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 flex items-center justify-between">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              Create {{ formatReportType(reportType) }} Report
            </h3>
            <button
              type="button"
              class="bg-white rounded-md text-gray-400 hover:text-gray-500"
              @click="close">
              <i class="fas fa-times" />
            </button>
          </div>

          <!-- Body -->
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6">
            <!-- Report Type Display (Read-only) -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <div class="mt-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md">
                <span class="text-gray-900">{{ formatReportType(formData.report_type) }}</span>
                <span class="text-gray-500 text-sm ml-2">(Automatically determined based on report history)</span>
              </div>
            </div>

            <!-- Title -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Report Title
              </label>
              <input
                v-model="formData.title"
                type="text"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., January 2024 Progress Report"
                required>
            </div>

            <!-- Heading -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Report Heading
              </label>
              <input
                v-model="formData.heading"
                type="text"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., Your Monthly Progress Update">
            </div>

            <!-- Intro Content -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Introduction Content
              </label>
              <textarea
                v-model="formData.intro_content"
                rows="3"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Brief introduction to the report..." />
            </div>

            <!-- Main Content -->
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Main Content
              </label>
              <textarea
                v-model="formData.content"
                rows="6"
                class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Main report content..." />
            </div>

            <!-- Moments/Checkins Section -->
            <div class="border-t pt-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-medium text-gray-900">
                  Moments/Checkins Statistics
                </h4>
                <label class="flex items-center">
                  <input
                    v-model="formData.include_moments_stats"
                    type="checkbox"
                    class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
                  <span class="ml-2 text-sm text-gray-700">Include statistics</span>
                </label>
              </div>

              <div
                v-if="formData.include_moments_stats"
                class="space-y-4">
                <!-- Date Range -->
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <input
                      v-model="formData.moments_start_date"
                      type="date"
                      class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      v-model="formData.moments_end_date"
                      type="date"
                      class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                  </div>
                </div>

                <!-- Preview Button -->
                <button
                  type="button"
                  :disabled="!formData.moments_start_date || !formData.moments_end_date"
                  class="w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  @click="previewMomentsStats">
                  <i class="fas fa-chart-bar mr-2" />
                  Preview Statistics
                </button>

                <!-- Moments List -->
                <div
                  v-if="availableMoments.length > 0"
                  class="mt-4">
                  <div class="flex items-center justify-between mb-3">
                    <h5 class="text-sm font-medium text-gray-900">
                      Available Moments ({{ availableMoments.length }})
                    </h5>
                    <div class="flex gap-2">
                      <button
                        type="button"
                        class="text-xs text-indigo-600 hover:text-indigo-800"
                        @click="selectAllMoments">
                        Select All
                      </button>
                      <button
                        type="button"
                        class="text-xs text-gray-600 hover:text-gray-800"
                        @click="deselectAllMoments">
                        Deselect All
                      </button>
                    </div>
                  </div>

                  <div class="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                    <div
                      v-for="moment in availableMoments"
                      :key="moment.id"
                      class="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
                      <label class="flex items-start p-3 cursor-pointer">
                        <input
                          v-model="selectedMomentIds"
                          type="checkbox"
                          :value="moment.id"
                          class="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
                        <div class="ml-3 flex-1">
                          <div class="flex items-center justify-between">
                            <div class="flex-1">
                              <p class="text-sm font-medium text-gray-900">
                                {{ moment.title }}
                              </p>
                              <p class="text-xs text-gray-500 mt-0.5">
                                {{ formatMomentDate(moment.scheduled_at) }}
                              </p>
                            </div>
                            <span
                              v-if="moment.has_images"
                              class="text-xs text-green-600 ml-2">
                              <i class="fas fa-camera" />
                              <span v-if="moment.images && moment.images.length">
                                {{ moment.images.length }}
                              </span>
                            </span>
                          </div>
                          <p class="text-xs text-gray-600 mt-1">
                            <span
                              v-if="moment.location_name"
                              class="mr-3">
                              <i class="fas fa-map-marker-alt" /> {{ moment.location_name }}
                            </span>
                            <span
                              v-if="moment.category"
                              class="mr-3">
                              <i class="fas fa-tag" /> {{ moment.category }}
                            </span>
                            <span v-if="moment.employee_name">
                              <i class="fas fa-user" /> {{ moment.employee_name }}
                            </span>
                          </p>
                          <p
                            v-if="moment.description"
                            class="text-xs text-gray-500 mt-1">
                            {{ moment.description.substring(0, 100) }}{{ moment.description.length > 100 ? '...' : '' }}
                          </p>

                          <!-- Image thumbnails -->
                          <div
                            v-if="moment.images && moment.images.length > 0"
                            class="flex gap-1 mt-2">
                            <img
                              v-for="(image, idx) in moment.images.slice(0, 3)"
                              :key="idx"
                              :src="image.url || image.thumbnail_url || image"
                              class="h-12 w-12 object-cover rounded border border-gray-200"
                              @error="handleImageError">
                            <span
                              v-if="moment.images.length > 3"
                              class="flex items-center justify-center h-12 w-12 bg-gray-100 rounded border border-gray-200 text-xs text-gray-600">
                              +{{ moment.images.length - 3 }}
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div class="mt-2 text-sm text-gray-600">
                    {{ selectedMomentIds.length }} of {{ availableMoments.length }} moments selected
                  </div>
                </div>

                <!-- Empty State -->
                <div
                  v-else-if="statsPreview && statsPreview.total === 0"
                  class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p class="text-sm text-yellow-800">
                    No moments found in the selected date range.
                  </p>
                </div>
              </div>
            </div>

            <!-- Citations Section -->
            <div class="border-t pt-6 mt-6">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-medium text-gray-900">
                  Citations
                </h4>
                <button
                  type="button"
                  class="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  @click="addCitation">
                  <i class="fas fa-plus mr-2" />
                  Add Citation
                </button>
              </div>

              <div
                v-if="citations.length > 0"
                class="space-y-3">
                <div
                  v-for="(citation, index) in citations"
                  :key="index"
                  class="border border-gray-200 rounded-lg p-3">
                  <div class="grid grid-cols-2 gap-3">
                    <input
                      v-model="citation.source_name"
                      type="text"
                      placeholder="Source Name"
                      class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                    <input
                      v-model="citation.url"
                      type="url"
                      placeholder="URL"
                      class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                  </div>
                  <div class="mt-2">
                    <input
                      v-model="citation.title"
                      type="text"
                      placeholder="Title"
                      class="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md">
                  </div>
                  <div class="mt-2 flex justify-between">
                    <input
                      v-model="citation.description"
                      type="text"
                      placeholder="Description (optional)"
                      class="flex-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md mr-2">
                    <button
                      type="button"
                      class="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      @click="removeCitation(index)">
                      <i class="fas fa-trash" />
                    </button>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="text-center py-4 text-gray-500 text-sm">
                No citations added yet
              </div>
            </div>

            <!-- Public/Private Toggle -->
            <div class="mt-6 flex items-center">
              <input
                v-model="formData.is_public"
                type="checkbox"
                class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded">
              <label class="ml-2 block text-sm text-gray-900">
                Make this report publicly accessible
              </label>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="submit"
              :disabled="isSubmitting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50">
              {{ isSubmitting ? 'Creating...' : 'Create Report' }}
            </button>
            <button
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              @click="close">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import mdtoast from '@dmuy/toast'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  reportType: {
    type: String,
    default: 'month_1'
  }
})

const emit = defineEmits(['close', 'reportCreated'])

// State
const formData = ref({
  report_type: props.reportType || 'month_1',
  title: '',
  heading: '',
  intro_content: '',
  content: '',
  include_moments_stats: false,
  moments_start_date: '',
  moments_end_date: '',
  is_public: true
})

const citations = ref([])
const statsPreview = ref(null)
const isSubmitting = ref(false)
const availableMoments = ref([])
const selectedMomentIds = ref([])

// Methods
const addCitation = () => {
  citations.value.push({
    source_name: '',
    url: '',
    title: '',
    description: '',
    position: citations.value.length
  })
}

const removeCitation = (index) => {
  citations.value.splice(index, 1)
  // Update positions
  citations.value.forEach((c, i) => {
    c.position = i
  })
}

const previewMomentsStats = async() => {
  if (!formData.value.moments_start_date || !formData.value.moments_end_date) {
    return
  }

  try {
    // This would call the available_moments endpoint
    const response = await axios.get(
      `/ajax/organizations/${props.organizationId}/monthly_reports/available_moments`,
      {
        params: {
          start_date: formData.value.moments_start_date,
          end_date: formData.value.moments_end_date
        }
      }
    )

    // Populate available moments and auto-select all
    availableMoments.value = response.data.moments || []
    selectedMomentIds.value = availableMoments.value.map((m) => m.id)

    statsPreview.value = {
      total: response.data.count,
      moments: response.data.moments
    }
  } catch (err) {
    mdtoast.error('Failed to preview statistics', { duration: 3000 })
  }
}

const submitReport = async() => {
  if (!formData.value.title) {
    mdtoast.error('Please enter a report title', { duration: 3000 })
    return
  }

  isSubmitting.value = true

  try {
    const response = await axios.post(
      `/ajax/organizations/${props.organizationId}/monthly_reports`,
      {
        monthly_report: formData.value,
        citations: citations.value.filter((c) => c.source_name && c.url),
        moment_ids: selectedMomentIds.value
      }
    )

    emit('reportCreated', response.data.report)
    close()
  } catch (err) {
    mdtoast.error('Failed to create report. Please try again.', { duration: 3000 })
    console.error('Error creating report:', err)
  } finally {
    isSubmitting.value = false
  }
}

const close = () => {
  emit('close')
}

const formatReportType = (type) => {
  const typeMap = {
    month_1: 'Month 1',
    month_2: 'Month 2',
    month_3_plus: 'Month 3+'
  }
  return typeMap[type] || type
}

const formatDateRange = () => {
  if (!formData.value.moments_start_date || !formData.value.moments_end_date) {
    return 'Not selected'
  }
  const start = new Date(formData.value.moments_start_date).toLocaleDateString()
  const end = new Date(formData.value.moments_end_date).toLocaleDateString()
  return `${start} - ${end}`
}

// Moments selection helpers
const selectAllMoments = () => {
  selectedMomentIds.value = availableMoments.value.map((m) => m.id)
}

const deselectAllMoments = () => {
  selectedMomentIds.value = []
}

const formatMomentDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

const handleImageError = (event) => {
  // Hide broken images
  event.target.style.display = 'none'
}

// Initialize form defaults based on report type
const initializeFormDefaults = () => {
  const today = new Date()
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December']
  const currentMonth = monthNames[today.getMonth()]
  const currentYear = today.getFullYear()

  if (props.reportType === 'month_1') {
    formData.value.title = `${currentMonth} ${currentYear} - First Month Progress Report`
    formData.value.heading = 'Your First Month with Linda'
    formData.value.intro_content = 'This report summarizes your organization\'s progress during the first month of using Linda\'s platform.'
  } else if (props.reportType === 'month_2') {
    formData.value.title = `${currentMonth} ${currentYear} - Second Month Progress Report`
    formData.value.heading = 'Continuing Your Success'
    formData.value.intro_content = 'This report highlights the continued progress and improvements in your second month.'
  } else {
    formData.value.title = `${currentMonth} ${currentYear} - Monthly Progress Report`
    formData.value.heading = 'Monthly Performance Overview'
    formData.value.intro_content = 'This report provides an overview of your organization\'s activities and achievements this month.'
  }
}

// Set default date ranges based on report type
const setDefaultDateRange = () => {
  const today = new Date()
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
  const sixtyDaysAgo = new Date(today.getTime() - 60 * 24 * 60 * 60 * 1000)

  switch (formData.value.report_type) {
    case 'month_1':
      // First 30 days
      formData.value.moments_start_date = thirtyDaysAgo.toISOString().split('T')[0]
      formData.value.moments_end_date = today.toISOString().split('T')[0]
      break
    case 'month_2':
      // Days 31-60
      formData.value.moments_start_date = sixtyDaysAgo.toISOString().split('T')[0]
      formData.value.moments_end_date = thirtyDaysAgo.toISOString().split('T')[0]
      break
    case 'month_3_plus':
      // Last 30 days
      formData.value.moments_start_date = thirtyDaysAgo.toISOString().split('T')[0]
      formData.value.moments_end_date = today.toISOString().split('T')[0]
      break
  }
}

// Lifecycle
onMounted(() => {
  initializeFormDefaults()
  setDefaultDateRange()
})
</script>
